import React, { useContext, useEffect, useState } from "react";
import { Row, Col, FormLabel, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";
import PageTitle from "../../../layouts/PageTitle";
import axiosInstance from "../../../../services/AxiosInstance";
import Api from "../../../../constants/Api";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { convertDoubleToStringPTBR } from "../../../../utils/ConverterUtils";
import { errorAlert } from "../../../components/alert/defaultAlert";
import SearchIcon from "../../../components/Icon/SearchIcon";
import {
    SubscriptionPanel,
    SubscriptionWorkspace,
} from "../common/SubscriptionWorkspace";
import { ThemeContext } from "../../../../context/ThemeContext";

const getPresetDates = (key) => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(start);
    if (key === "today") return [start, end];
    if (key === "last7") start.setDate(start.getDate() - 6);
    if (key === "last30") start.setDate(start.getDate() - 29);
    if (key === "month") return [new Date(today.getFullYear(), today.getMonth(), 1), new Date(today.getFullYear(), today.getMonth() + 1, 0)];
    if (key === "lastMonth") return [new Date(today.getFullYear(), today.getMonth() - 1, 1), new Date(today.getFullYear(), today.getMonth(), 0)];
    if (key === "year") return [new Date(today.getFullYear(), 0, 1), end];
    return [start, end];
};

const PERIOD_OPTIONS = [
    ["today", "today"], ["last7", "period_shortcut.last_7_days"], ["last30", "last_30_days"],
    ["month", "period_shortcut.this_month"], ["lastMonth", "period_shortcut.last_month"],
    ["year", "period_shortcut.this_year"], ["custom", "custom_period"]
];

const isValidDate = (value) => value instanceof Date && !Number.isNaN(value.getTime());
const toInputDate = (value) => isValidDate(value) ? formatDateToYYYYMMDD(value) : '';
const fromInputDate = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return isValidDate(date) ? date : null;
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const SubscriptionDashboard = () => {
    const { t } = useTranslation();
    const { appTheme } = useContext(ThemeContext);
    const isDarkTheme = appTheme === "dark";
    const chartTextColor = isDarkTheme ? "#d8d2ce" : "#4f4842";
    const chartGridColor = isDarkTheme ? "rgba(242, 238, 234, 0.13)" : "rgba(31, 32, 36, 0.10)";
    const chartTooltipBackground = isDarkTheme ? "#242327" : "#ffffff";

    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [generalStats, setGeneralStats] = useState(null);
    const [periodStats, setPeriodStats] = useState([]);
    const [currentMonthStats, setCurrentMonthStats] = useState([]);
    const [planStats, setPlanStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [periodPreset, setPeriodPreset] = useState("year");

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        loadInitialData();
    }, []);
    /* eslint-enable react-hooks/exhaustive-deps */

    const loadInitialData = () => {
        setLoading(true);
        Promise.all([
            loadGeneralStats(),
            loadCurrentYearStats(),
            loadCurrentMonthStats(),
            loadStatsByPlan()
        ]).finally(() => setLoading(false));
    };

    const loadGeneralStats = () => {
        return axiosInstance.get(`${Api.subscription.analytics}/general`)
            .then((response) => setGeneralStats(response.data))
            .catch((error) => errorAlert(error));
    };

    const loadCurrentYearStats = () => {
        return axiosInstance.get(`${Api.subscription.analytics}/current-year`)
            .then((response) => setPeriodStats(response.data))
            .catch((error) => errorAlert(error));
    };

    const loadCurrentMonthStats = () => {
        return axiosInstance.get(`${Api.subscription.analytics}/current-month`)
            .then((response) => setCurrentMonthStats(response.data ? [response.data] : []))
            .catch((error) => errorAlert(error));
    };

    const loadStatsByPlan = () => {
        return axiosInstance.get(`${Api.subscription.analytics}/by-plan`)
            .then((response) => setPlanStats(response.data))
            .catch((error) => errorAlert(error));
    };

    const loadStatsByPeriod = () => {
        if (!isValidDate(startDate) || !isValidDate(endDate) || startDate > endDate) {
            return;
        }

        const params = {
            startDate: `${formatDateToYYYYMMDD(startDate)}T00:00:00`,
            endDate: `${formatDateToYYYYMMDD(endDate)}T23:59:59`
        };

        setLoading(true);
        axiosInstance.get(`${Api.subscription.analytics}/by-period`, { params })
            .then((response) => setPeriodStats(response.data))
            .catch((error) => errorAlert(error))
            .finally(() => setLoading(false));
    };

    const selectPeriodPreset = (key) => {
        setPeriodPreset(key);
        if (key !== "custom") {
            const [start, end] = getPresetDates(key);
            setStartDate(start);
            setEndDate(end);
        }
    };

    const formatMoney = (value) => `R$ ${convertDoubleToStringPTBR(Number(value || 0))}`;

    // Cancelado é o único card que respeita o período exibido no gráfico ao lado
    // (ano atual por padrão, ou o período buscado) - os demais são sempre gerais,
    // mesmo critério aplicado na tela de assinantes.
    const hasCustomPeriod = Boolean(startDate && endDate);
    // /current-month devolve um objeto unico, guardado em array so para reaproveitar o
    // mesmo reduce do by-period. O valor cancelado mora no proprio objeto, entao precisa
    // sair do primeiro item - ler direto do array devolvia undefined e zerava o card.
    const currentMonthSummary = currentMonthStats[0] || null;
    const cancellationStats = hasCustomPeriod ? periodStats : currentMonthStats;
    const canceledInPeriod = cancellationStats.reduce((sum, stat) => sum + (stat.canceledSubscriptions || 0), 0);
    const canceledRevenueInPeriod = hasCustomPeriod
        ? cancellationStats.reduce((sum, stat) => sum + (stat.canceledRevenue || 0), 0)
        : (currentMonthSummary?.canceledMonthlyRevenue || 0);
    const cancellationPeriodLabel = hasCustomPeriod
        ? `${startDate.toLocaleDateString("pt-BR")} a ${endDate.toLocaleDateString("pt-BR")}`
        : `01/${String(new Date().getMonth() + 1).padStart(2, "0")}/${new Date().getFullYear()} a ${new Date().toLocaleDateString("pt-BR")}`;

    const summaryCards = [
        {
            label: t("active_subscriptions"),
            value: generalStats?.activeSubscriptions || 0,
            valueLabel: formatMoney(generalStats?.totalMonthlyRevenue),
            icon: "fa-users",
            tone: "sky"
        },
        {
            // "Em dia" = ativas SEM contar as atrasadas (card seguinte já mostra as
            // atrasadas separado) - mesmo critério aplicado na tela de assinantes.
            label: t("subscription_subscribers_on_time_label"),
            value: (generalStats?.activeSubscriptions || 0) - (generalStats?.overdueSubscriptions || 0),
            valueLabel: formatMoney((generalStats?.totalMonthlyRevenue || 0) - (generalStats?.overdueMonthlyRevenue || 0)),
            icon: "fa-circle-check",
            tone: "green"
        },
        {
            label: t("overdue_subscriptions"),
            value: generalStats?.overdueSubscriptions || 0,
            valueLabel: formatMoney(generalStats?.overdueMonthlyRevenue),
            icon: "fa-triangle-exclamation",
            tone: "coral"
        },
        {
            label: t("subscription_cancellations_in_period"),
            value: canceledInPeriod,
            valueLabel: formatMoney(canceledRevenueInPeriod),
            detail: cancellationPeriodLabel,
            icon: "fa-ban",
            tone: "slate"
        },
        {
            label: t("subscription_average_ticket"),
            value: formatMoney(generalStats?.averageSubscriptionValue),
            icon: "fa-receipt",
            tone: "green"
        }
    ];

    const periodChartData = {
        labels: periodStats.map((stat) => stat.monthName || `${stat.month}/${stat.year}`),
        datasets: [
            {
                label: t("new_subscriptions"),
                data: periodStats.map((stat) => stat.newSubscriptions),
                borderColor: "rgba(14, 165, 233, 1)",
                backgroundColor: "rgba(14, 165, 233, 0.16)",
                tension: 0.35,
                fill: true
            },
            {
                label: t("cancellations"),
                data: periodStats.map((stat) => stat.canceledSubscriptions),
                borderColor: "rgba(248, 113, 113, 1)",
                backgroundColor: "rgba(248, 113, 113, 0.16)",
                tension: 0.35,
                fill: true
            }
        ]
    };

    const periodChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: { color: chartTextColor }
            },
            tooltip: {
                backgroundColor: chartTooltipBackground,
                titleColor: chartTextColor,
                bodyColor: chartTextColor,
                borderColor: chartGridColor,
                borderWidth: 1
            },
            title: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    color: chartTextColor
                },
                grid: { color: chartGridColor }
            },
            x: {
                ticks: { color: chartTextColor },
                grid: { color: chartGridColor }
            }
        }
    };

    const planChartData = {
        labels: planStats.map((stat) => stat.planName),
        datasets: [
            {
                label: t("active_subscriptions"),
                data: planStats.map((stat) => stat.activeSubscriptions),
                backgroundColor: "rgba(14, 165, 233, 0.78)",
                borderRadius: 10
            },
            {
                label: t("cancelled_subscriptions"),
                data: planStats.map((stat) => stat.canceledSubscriptions),
                backgroundColor: "rgba(248, 113, 113, 0.72)",
                borderRadius: 10
            }
        ]
    };

    const planChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top",
                labels: { color: chartTextColor }
            },
            tooltip: {
                backgroundColor: chartTooltipBackground,
                titleColor: chartTextColor,
                bodyColor: chartTextColor,
                borderColor: chartGridColor,
                borderWidth: 1
            },
            title: {
                display: false
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    color: chartTextColor
                },
                grid: { color: chartGridColor }
            },
            x: {
                ticks: { color: chartTextColor },
                grid: { color: chartGridColor }
            }
        }
    };

    const activeInGoodStanding = (generalStats?.activeSubscriptions || 0) - (generalStats?.overdueSubscriptions || 0);
    const overdueCount = generalStats?.overdueSubscriptions || 0;
    const canceledCount = generalStats?.canceledSubscriptions || 0;

    const statusChartData = {
        labels: [t("active_in_good_standing"), t("overdue_subscriptions"), t("cancelled_subscriptions")],
        datasets: [
            {
                data: [activeInGoodStanding, overdueCount, canceledCount],
                backgroundColor: [
                    "rgba(34, 197, 94, 0.78)", // Green
                    "rgba(248, 113, 113, 0.78)", // Coral
                    "rgba(148, 163, 184, 0.72)" // Slate
                ],
                borderColor: [
                    "rgba(34, 197, 94, 1)",
                    "rgba(248, 113, 113, 1)",
                    "rgba(148, 163, 184, 1)"
                ],
                borderWidth: 1.5,
                hoverOffset: 4
            }
        ]
    };

    const statusChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    boxWidth: 12,
                    padding: 15,
                    color: chartTextColor
                }
            },
            tooltip: {
                backgroundColor: chartTooltipBackground,
                titleColor: chartTextColor,
                bodyColor: chartTextColor,
                borderColor: chartGridColor,
                borderWidth: 1
            },
            title: {
                display: false
            }
        },
        cutout: "70%"
    };

    return (
        <>
            <PageTitle activeMenu={t("dashboard")} motherMenu={t("subscriptions")} />

            <SubscriptionWorkspace
                // O breadcrumb já identifica Assinaturas e os cards mostram os
                // indicadores imediatamente; o hero repetia a mesma informação.
                showHeader={false}
                summaryCards={summaryCards}
                cardsLoading={loading}
            >
                <SubscriptionPanel
                    title={t("period_filter")}
                    subtitle={t("subscription_dashboard_filter_hint")}
                    className="subscription-workspace__filters"
                >
                    <Row className="g-2 align-items-end subscription-workspace__filter-row">
                        <Col xs={12}>
                            <div className="dashboard-scope-filters__presets">
                                {PERIOD_OPTIONS.map(([key, labelKey]) => (
                                    <button key={key} type="button"
                                        className={`dashboard-scope-chip ${periodPreset === key ? "is-active" : ""}`}
                                        onClick={() => selectPeriodPreset(key)}>
                                        {t(labelKey)}
                                    </button>
                                ))}
                            </div>
                        </Col>
                        <Col xs={12} sm={6} md="auto" className="subscription-workspace__filter-date-col">
                            <FormLabel>{t("start_date")}</FormLabel>
                            <div className="subscription-workspace__filter-date">
                                <input className="form-control" type="date" value={toInputDate(startDate)}
                                    onChange={(event) => setStartDate(fromInputDate(event.target.value))} />
                            </div>
                        </Col>
                        <Col xs={12} sm={6} md="auto" className="subscription-workspace__filter-date-col">
                            <FormLabel>{t("end_date")}</FormLabel>
                            <div className="subscription-workspace__filter-date">
                                <input className="form-control" type="date" value={toInputDate(endDate)}
                                    onChange={(event) => setEndDate(fromInputDate(event.target.value))} />
                            </div>
                        </Col>
                        <Col xs={12} md className="subscription-workspace__filter-actions-col">
                            <div className="subscription-workspace__filter-actions">
                                <Button
                                    type="button"
                                    variant="primary"
                                    onClick={loadStatsByPeriod}
                                    disabled={!startDate || !endDate}
                                >
                                    <SearchIcon /> {t("search")}
                                </Button>
                                <Button
                                    type="button"
                                    variant="light"
                                    onClick={() => {
                                        setStartDate(null);
                                        setEndDate(null);
                                        loadCurrentYearStats();
                                    }}
                                >
                                    {t("clear")}
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </SubscriptionPanel>

                <Row className="g-3 mb-3">
                    <Col xl={8} className="d-flex">
                        <SubscriptionPanel
                            title={startDate && endDate ? t("subscriptions_in_period") : t("subscriptions_current_year")}
                            subtitle={t("subscription_dashboard_period_hint")}
                            className="flex-grow-1"
                        >
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t("loading")}...</span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ height: "300px" }}>
                                    <Line data={periodChartData} options={periodChartOptions} />
                                </div>
                            )}
                        </SubscriptionPanel>
                    </Col>
                    <Col xl={4} className="d-flex">
                        <SubscriptionPanel
                            title={t("subscription_status_distribution")}
                            subtitle={t("subscription_dashboard_status_hint")}
                            className="flex-grow-1"
                        >
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t("loading")}...</span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ height: "300px" }}>
                                    <Doughnut data={statusChartData} options={statusChartOptions} />
                                </div>
                            )}
                        </SubscriptionPanel>
                    </Col>
                </Row>

                <Row className="g-3">
                    <Col xl={12} className="d-flex">
                        <SubscriptionPanel
                            title={t("subscriptions_by_plan")}
                            subtitle={t("subscription_dashboard_plan_hint")}
                            className="flex-grow-1"
                        >
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">{t("loading")}...</span>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ height: "280px" }}>
                                    <Bar data={planChartData} options={planChartOptions} />
                                </div>
                            )}
                        </SubscriptionPanel>
                    </Col>
                </Row>
            </SubscriptionWorkspace>
        </>
    );
};

export default SubscriptionDashboard;
